import { Segment, User } from '../types'

/**
 * Parses a string of text to find mentions and returns an array of segments.
 *
 * @param text The input text to parse.
 * @param users The list of all possible users to match against.
 * @param nextId A mutable ref object to get the next unique ID for a segment.
 * @returns An array of Segments.
 */
export const parsePastedTextToSegments = (
  text: string,
  users: User[],
  nextId: React.MutableRefObject<number>,
): Segment[] => {
  if (!text) {
    return []
  }

  const segments: Segment[] = []
  let lastIndex = 0

  // Sort users by name length, descending. This is crucial for matching longer names first
  // e.g., to match "@John Doe" before "@John".
  const sortedUsers = users.slice().sort((a, b) => b.name.length - a.name.length)

  interface FoundMention {
    start: number
    end: number
    user: User
  }

  const foundMentions: FoundMention[] = []

  // Find all possible mentions
  for (const user of sortedUsers) {
    const mention = `@${user.name}`
    let startIndex = text.indexOf(mention)
    while (startIndex !== -1) {
      const endIndex = startIndex + mention.length
      // Check if this found mention overlaps with an already found, longer one.
      const isOverlapping = foundMentions.some(
        found => (startIndex >= found.start && startIndex < found.end) || (endIndex > found.start && endIndex <= found.end),
      )

      if (!isOverlapping) {
        foundMentions.push({ start: startIndex, end: endIndex, user })
      }
      // Continue searching from the end of the current found mention
      startIndex = text.indexOf(mention, endIndex)
    }
  }

  // Sort found mentions by their start index
  foundMentions.sort((a, b) => a.start - b.start)

  // Create segments based on found mentions
  for (const mention of foundMentions) {
    // Add text segment before the mention
    if (mention.start > lastIndex) {
      segments.push({
        id: `seg-${nextId.current++}`,
        type: 'text',
        content: text.substring(lastIndex, mention.start),
      })
    }

    // Add the mention segment
    segments.push({
      id: `seg-${nextId.current++}`,
      type: 'mention',
      content: text.substring(mention.start, mention.end),
    })

    lastIndex = mention.end
  }

  // Add the remaining text segment
  if (lastIndex < text.length) {
    segments.push({
      id: `seg-${nextId.current++}`,
      type: 'text',
      content: text.substring(lastIndex),
    })
  }

  // If text is not empty but no segments were created (e.g. no mentions), create a single text segment
  if (segments.length === 0 && text.length > 0) {
    segments.push({
      id: `seg-${nextId.current++}`,
      type: 'text',
      content: text,
    })
  }

  return segments
} 